/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { PropsWithChildren } from 'react'

import styled, { css } from 'styled-components'

import FlexBox from 'src/components/Common/FlexBox'
import { pxToVw } from 'src/functions/units'

import { ModalV2Config } from '../modal-v2.context'
import { useModalV2Context } from '../modal-v2.hooks'
import CloseButton from './CloseButton'
import Footer from './Footer'
import Header from './Header'

type Props = PropsWithChildren<Omit<ModalV2Config, 'content'>>

const DismissableDialog = ({
  children,
  dialogTitle = '',
  dialogStyle,
  fullscreen = false,
  showCloseButton = false,
  showDialogFooter = false,
  showDialogHeader = false
}: Props) => {
  const { closeTopmostModal } = useModalV2Context()

  return (
    <StyledDialog
      className={fullscreen ? 'animate__animated animate__faster animate__slideInUp' : undefined}
      $fullscreen={fullscreen}
      flexDirection={'column'}
      style={dialogStyle}
    >
      <CloseButton
        hidden={!showCloseButton}
        size={fullscreen ? 48 : 24}
        style={
          fullscreen && {
            top: pxToVw(72),
            right: pxToVw(72)
          }
        }
        onClick={closeTopmostModal}
      />

      <Header hidden={!showDialogHeader} title={dialogTitle} />

      <StyledDialogContent>
        {children}
        <Footer hidden={!showDialogFooter} />
      </StyledDialogContent>
    </StyledDialog>
  )
}

export default DismissableDialog

const StyledDialog = styled(FlexBox)<{
  $fullscreen: boolean
  $asWidgetContainer?: boolean
  style: Props['dialogStyle']
}>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${({ theme, $fullscreen, $asWidgetContainer = false }) => css`
    background-color: ${$asWidgetContainer ? 'transparent' : theme.colors.bg};
    border-radius: ${$fullscreen || $asWidgetContainer ? 0 : theme.units.borderRadius.medium};
    width: ${$fullscreen ? '100vw' : '36.4583vw'};
    min-width: ${$fullscreen ? 'auto' : '420px'};
    // max-width: ${$fullscreen ? 'auto' : '700px'};
    height: ${$fullscreen ? '100vh' : $asWidgetContainer ? '85vh' : 'auto'};
    max-height: ${$fullscreen || $asWidgetContainer ? 'auto' : '85vh'};
    padding: ${$fullscreen || $asWidgetContainer ? 0 : '2.083vw 0 0'};

    ${$asWidgetContainer &&
    css`
      ${StyledDialogContent} {
        padding: 0;
        height: 100%;
        overflow: hidden;
      }
    `}

    ${$fullscreen &&
    css`
      ${StyledDialogContent} {
        padding-block: 0;
        padding: 1.5625vw 0 0;
      }
    `}
  `}

  ${({ theme, $fullscreen, $asWidgetContainer }) => css`
    ${theme.mediaQuery.mobile`
      width: 100vw;
      min-width: unset;
      // max-width: unset;
      top: unset;
      bottom: 0;
      left: 0;
      transform: translate(0, 0);
      padding-top: ${!$fullscreen && !$asWidgetContainer && pxToVw(40, 720)};
    `}
  `}

  ${({ style }) => style}
`

const StyledDialogContent = styled.div`
  width: 100%;
  overflow: auto;
  padding: ${pxToVw(30)} ${pxToVw(40)} 0;
  position: relative;

  /*
  ::-webkit-scrollbar {
    display: none; // Safari and Chrome
  }
  -ms-overflow-style: none; // IE and Edge
  scrollbar-width: none; // Firefox
  */

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      padding: ${pxToVw(30, 720)} ${pxToVw(40, 720)} 0;
    `}
  `}
`
